import { useState, useCallback } from 'react';
import { SubscriptionPlan } from '../constants/Subscriptions';
import {
  switchSubscriptionPlan,
  getCurrentPlan,
  validatePlanSwitch,
  mergeCompanyStripeSubscription,
} from '../services/subscriptionService';
import { useAuth } from '../contexts/AuthContext';
import {
  createCheckout,
  createCustomer,
  getStatus,
  redeemEnterpriseUpgradeCode,
  syncEnterpriseStripeMetadataForCompany,
} from '../services/stripeWorkerClient';
import {
  getPriceIdForPlan,
  getPortalBillingUrls,
  planCategoryToWorkerCategory,
  planIdToWorkerSku,
} from '../constants/stripePrices';

interface UsePlanSwitchingResult {
  currentPlan: SubscriptionPlan | null;
  loading: boolean;
  error: string | null;
  loadCurrentPlan: () => Promise<void>;
  switchPlan: (
    newPlan: SubscriptionPlan,
    isAnnual?: boolean,
    options?: { enterpriseCode?: string }
  ) => Promise<void>;
  validateSwitch: (newPlan: SubscriptionPlan) => Promise<{ canSwitch: boolean; reason?: string }>;
  clearError: () => void;
}

export const usePlanSwitching = (): UsePlanSwitchingResult => {
  const { userProfile } = useAuth();
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCurrentPlan = useCallback(async () => {
    if (!userProfile?.masterOwnerID) {
      setError('User profile not found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const plan = await getCurrentPlan(userProfile.masterOwnerID);
      setCurrentPlan(plan);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load current plan';
      setError(message);
      console.error('Error loading current plan:', err);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.masterOwnerID]);

  const switchPlan = useCallback(
    async (
      newPlan: SubscriptionPlan,
      isAnnual: boolean = false,
      options?: { enterpriseCode?: string }
    ) => {
      if (!userProfile?.masterOwnerID) {
        throw new Error('User profile not found');
      }
      if (!userProfile?.email) {
        throw new Error('User email is required for billing. Please update your profile.');
      }

      setLoading(true);
      setError(null);

      try {
        const validation = await validatePlanSwitch(userProfile.masterOwnerID, newPlan);
        if (!validation.canSwitch) {
          throw new Error(validation.reason || 'Plan switch not allowed');
        }

        const isEnterprise =
          newPlan.id === 'Corp-Enterprise' ||
          newPlan.id === 'LEO-Enterprise' ||
          (newPlan.price === 0 && newPlan.id.toLowerCase().includes('enterprise'));

        if (isEnterprise) {
          const code = options?.enterpriseCode?.trim();
          if (!code) {
            throw new Error('Enter the enterprise activation code provided by SkimBuster.');
          }
          const category = planCategoryToWorkerCategory(newPlan.category);
          const redeemed = await redeemEnterpriseUpgradeCode({
            enterpriseCode: code,
            companyId: userProfile.masterOwnerID,
            email: userProfile.email,
            planId: newPlan.id,
            category,
          });
          if (!redeemed.ok) {
            throw new Error('Enterprise code was not accepted.');
          }
          await switchSubscriptionPlan(userProfile.masterOwnerID, newPlan, isAnnual);
          if (redeemed.subscription) {
            await mergeCompanyStripeSubscription(userProfile.masterOwnerID, redeemed.subscription);
          }
          const sub = redeemed.subscription;
          const stripeCustomerId = sub?.stripeCustomerId;
          if (stripeCustomerId && userProfile.email) {
            try {
              await syncEnterpriseStripeMetadataForCompany({
                stripeCustomerId,
                companyId: userProfile.masterOwnerID,
                userEmail: userProfile.email,
                stripeSubscriptionId: sub?.stripeSubscriptionId || undefined,
              });
            } catch (syncErr) {
              console.warn('Enterprise Stripe metadata sync skipped or failed:', syncErr);
            }
          }
          await loadCurrentPlan();
          return;
        }

        const priceId = getPriceIdForPlan(newPlan, isAnnual);
        if (!priceId) {
          throw new Error('No Stripe price is configured for this plan. Use Manage subscription or contact sales.');
        }

        const companyId = userProfile.masterOwnerID;
        const planSku = planIdToWorkerSku(newPlan.id);
        const category = planCategoryToWorkerCategory(newPlan.category);

        let { subscription } = await getStatus({ companyId });
        let stripeCustomerId = subscription?.stripeCustomerId;

        if (!stripeCustomerId) {
          await createCustomer(userProfile.email, companyId, planSku, category);
          ({ subscription } = await getStatus({ companyId }));
          stripeCustomerId = subscription?.stripeCustomerId;
        }

        if (!stripeCustomerId) {
          throw new Error('Could not create or load a Stripe customer for your company.');
        }

        const { successUrl, cancelUrl } = getPortalBillingUrls();
        const { checkoutUrl } = await createCheckout(stripeCustomerId, priceId, companyId, {
          successUrl,
          cancelUrl,
        });

        window.location.assign(checkoutUrl);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to switch plan';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userProfile?.masterOwnerID, userProfile?.email, loadCurrentPlan]
  );

  const validateSwitch = useCallback(
    async (newPlan: SubscriptionPlan) => {
      if (!userProfile?.masterOwnerID) {
        return { canSwitch: false, reason: 'User profile not found' };
      }

      try {
        return await validatePlanSwitch(userProfile.masterOwnerID, newPlan);
      } catch (err: unknown) {
        console.error('Error validating plan switch:', err);
        return { canSwitch: false, reason: 'Failed to validate plan switch' };
      }
    },
    [userProfile?.masterOwnerID]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    currentPlan,
    loading,
    error,
    loadCurrentPlan,
    switchPlan,
    validateSwitch,
    clearError,
  };
};

export default usePlanSwitching;
